/**
 * Gifthatke Product Personalizer (modal + WhatsApp version)
 * -----------------------------------------------------------------------
 * CHANGE FROM PREVIOUS VERSION:
 *   The modal's button is now the actual "Add to Cart" action (previously
 *   it just closed the modal and required a separate click on the store's
 *   native Add to Cart button). Clicking it now:
 *     1. Adds the product to the cart immediately (with any typed text
 *        attached instantly — no waiting).
 *     2. Opens the Impulse theme's cart drawer right away.
 *     3. Uploads the preview/print images to Cloudinary in the background,
 *        THEN attaches those URLs to the same cart line via
 *        /cart/change.js — this keeps going even if the drawer is closed
 *        or the customer navigates to another page, so the print file
 *        still reliably ends up on the order.
 *
 * SETUP REQUIRED:
 *   1. Free Cloudinary account + unsigned upload preset
 *   2. Fill in CLOUD_NAME and UPLOAD_PRESET below
 *   3. Fill in WHATSAPP_NUMBER below (your business WhatsApp, international
 *      format, digits only — e.g. "919876543210" for a +91 number)
 */

(function () {
  "use strict";

  // ====== CONFIG — fill these in ======
  const CLOUD_NAME = "efrcxmvo";
  const UPLOAD_PRESET = "Gift Hatke";
  const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`;
  const PRINT_EXPORT_MULTIPLIER = 3;
  const WHATSAPP_NUMBER = "919509903633"; // digits only, country code, no + or spaces
  // =====================================

  // One instance per personalizable piece (e.g. "Photo Frame")
  class PersonalizerItem {
    constructor(el) {
      this.el = el;
      this.label = el.dataset.itemLabel || "Item";
      this.type = el.dataset.itemType || "text"; // "text" | "photo" | "both"
      this.mockupUrl = el.dataset.itemMockup || "";

      this.canvasEl = el.querySelector("[data-personalizer-canvas]");
      this.textInput = el.querySelector("[data-personalizer-text]");
      this.fontSizeInput = el.querySelector("[data-personalizer-fontsize]");
      this.colorInput = el.querySelector("[data-personalizer-color]");
      this.fileInput = el.querySelector("[data-personalizer-file]");

      this.textObject = null;
      this.photoObject = null;

      this._init();
    }

    _init() {
      this.canvas = new fabric.Canvas(this.canvasEl);
      this._loadMockup();
      this._bindControls();
    }

    _loadMockup() {
      const maxW = this.canvasEl.width || 260;
      const maxH = this.canvasEl.height || 260;

      if (!this.mockupUrl) {
        this.canvas.setWidth(maxW);
        this.canvas.setHeight(maxH);
        this._applyResponsiveSize(maxW, maxH);
        return;
      }

      fabric.Image.fromURL(
        this.mockupUrl,
        (img) => {
          const scale = Math.min(maxW / img.width, maxH / img.height);
          const displayW = img.width * scale;
          const displayH = img.height * scale;

          this.canvas.setWidth(displayW);
          this.canvas.setHeight(displayH);
          img.scale(scale);
          img.set({ selectable: false, evented: false });
          this.canvas.setBackgroundImage(
            img,
            this.canvas.renderAll.bind(this.canvas)
          );
          this._applyResponsiveSize(displayW, displayH);
        },
        { crossOrigin: "anonymous" }
      );
    }

    _applyResponsiveSize(displayW, displayH) {
      const wrapper = this.canvas.wrapperEl;
      if (wrapper) {
        wrapper.style.width = "100%";
        wrapper.style.maxWidth = displayW + "px";
        wrapper.style.height = "auto";
        wrapper.style.aspectRatio = `${displayW} / ${displayH}`;
      }
      [this.canvas.lowerCanvasEl, this.canvas.upperCanvasEl].forEach((el) => {
        if (!el) return;
        el.style.width = "100%";
        el.style.height = "100%";
      });
    }

    _bindControls() {
      if (this.textInput) {
        this.textInput.addEventListener("input", () => this._updateText());
      }
      if (this.fontSizeInput) {
        this.fontSizeInput.addEventListener("input", () => this._updateText());
      }
      if (this.colorInput) {
        this.colorInput.addEventListener("input", () => this._updateText());
      }
      if (this.fileInput) {
        this.fileInput.addEventListener("change", (e) => {
          const file = e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (evt) => this._placePhoto(evt.target.result);
          reader.readAsDataURL(file);
        });
      }
    }

    _updateText() {
      const value = this.textInput ? this.textInput.value : "";
      if (!value) {
        if (this.textObject) {
          this.canvas.remove(this.textObject);
          this.textObject = null;
        }
        return;
      }
      if (!this.textObject) {
        this.textObject = new fabric.Textbox(value, {
          left: this.canvas.getWidth() / 2,
          top: this.canvas.getHeight() / 2,
          originX: "center",
          originY: "center",
          fontSize: this.fontSizeInput ? parseInt(this.fontSizeInput.value, 10) : 22,
          fill: this.colorInput ? this.colorInput.value : "#000000",
          fontFamily: "Georgia, serif",
          textAlign: "center",
        });
        this.canvas.add(this.textObject);
      } else {
        this.textObject.set({
          text: value,
          fontSize: this.fontSizeInput ? parseInt(this.fontSizeInput.value, 10) : 22,
          fill: this.colorInput ? this.colorInput.value : "#000000",
        });
      }
      this.canvas.renderAll();
    }

    _placePhoto(dataUrl) {
      fabric.Image.fromURL(dataUrl, (img) => {
        if (this.photoObject) this.canvas.remove(this.photoObject);
        img.scaleToWidth(this.canvas.getWidth() * 0.4);
        img.set({
          left: this.canvas.getWidth() / 4,
          top: this.canvas.getHeight() / 4,
          cornerColor: "black",
          cornerStyle: "circle",
          transparentCorners: false,
        });
        this.photoObject = img;
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
      });
    }

    hasCustomization() {
      return !!(this.textObject || this.photoObject);
    }

    getText() {
      return this.textInput ? this.textInput.value : "";
    }

    hasPhoto() {
      return !!this.photoObject;
    }

    exportImages() {
      const preview = this.canvas.toDataURL({ format: "png", quality: 0.9 });
      const print = this.canvas.toDataURL({
        format: "png",
        quality: 1,
        multiplier: PRINT_EXPORT_MULTIPLIER,
      });
      return { preview, print };
    }
  }

  // One instance per product page
  class PersonalizerManager {
    constructor(root) {
      this.root = root;
      // Optional — used only as a fallback for the native theme button below.
      // The modal's own "Add to Cart" flow no longer depends on this, since
      // this widget isn't necessarily nested inside the product's <form>.
      this.productForm = root.closest("form[action*='/cart/add']");
      this.statusEl = root.querySelector("[data-personalizer-status]");

      this.items = Array.from(
        root.querySelectorAll("[data-personalizer-item]")
      ).map((el) => new PersonalizerItem(el));

      this.triggerBtn = root.querySelector("[data-personalizer-trigger]");
      this.triggerText = root.querySelector("[data-personalizer-trigger-text]");
      this.modal = root.querySelector("[data-personalizer-modal]");
      this.closeEls = root.querySelectorAll("[data-personalizer-close]");
      this.saveBtn = root.querySelector("[data-personalizer-save]"); // now acts as "Add to Cart"
      this.whatsappBtn = root.querySelector("[data-personalizer-whatsapp]");
      this.productTitle = root.dataset.productTitle || document.title;
      this.productUrl = root.dataset.productUrl || window.location.href;

      this._bindModal();
      this._bindWhatsapp();
      this._bindNativeFormFallback();
    }

    // ---------- Modal ----------
    _bindModal() {
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener("click", () => this._openModal());
      }
      this.closeEls.forEach((el) =>
        el.addEventListener("click", () => this._closeModal())
      );
      if (this.saveBtn) {
        this.saveBtn.addEventListener("click", () => this._addToCartFromModal());
      }
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this._closeModal();
      });
    }

    _openModal() {
      if (!this.modal) return;
      this.modal.classList.add("is-open");
      document.body.style.overflow = "hidden";
    }

    _closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove("is-open");
      document.body.style.overflow = "";
    }

    _updateTriggerLabel() {
      if (!this.triggerText) return;
      const count = this.items.filter((i) => i.hasCustomization()).length;
      this.triggerText.textContent =
        count > 0
          ? `\u2713 Customized (${count}/${this.items.length}) \u2014 Edit`
          : "\uD83C\uDF81 Customize It Now";
    }

    // ---------- WhatsApp the details ----------
    _bindWhatsapp() {
      if (!this.whatsappBtn) return;
      this.whatsappBtn.addEventListener("click", () => this._sendToWhatsapp());
    }

    _sendToWhatsapp() {
      const lines = [
        `Hi! I'd like to enquire about: *${this.productTitle}*`,
        this.productUrl,
        "",
        "Customization details:",
      ];

      this.items.forEach((item) => {
        const text = item.getText();
        const photo = item.hasPhoto();
        if (!text && !photo) return;
        let line = `- ${item.label}:`;
        if (text) line += ` "${text}"`;
        if (photo) line += `${text ? "," : ""} photo uploaded (will send separately)`;
        lines.push(line);
      });

      if (lines[lines.length - 1] === "Customization details:") {
        lines.push("(No customization added yet)");
      }

      const message = lines.join("\n");
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }

    // ---------- Status helper ----------
    _setStatus(msg) {
      if (this.statusEl) this.statusEl.textContent = msg;
    }

    // ---------- Main flow: click "Add to Cart" inside the modal ----------
    async _addToCartFromModal() {
      const variantId = this.root.dataset.variantId;
      if (!variantId) {
        this._setStatus("Couldn't determine which variant to add. Please refresh and try again.");
        return;
      }
      if (this.saveBtn) this.saveBtn.disabled = true;
      this._setStatus("Adding to cart...");

      // Only what's available INSTANTLY (typed text) goes in right away.
      // Image URLs are attached afterward, once Cloudinary finishes.
      const instantProps = {};
      this.items.forEach((item) => {
        const text = item.getText();
        if (text) instantProps[`${item.label} - Text`] = text;
      });

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: instantProps,
          }),
        });
        if (!res.ok) throw new Error("Add to cart failed: " + res.status);
        const addedItem = await res.json(); // { key, quantity, ... }

        this._closeModal();
        this._updateTriggerLabel();
        this._setStatus("");
        if (this.saveBtn) this.saveBtn.disabled = false;

        this._openCartDrawer();

        // Fire-and-forget: keeps running even if the drawer closes or the
        // customer navigates away. Errors are logged, not shown to the
        // customer, since the product is already successfully in their cart.
        this._uploadAndAttach(addedItem.key, addedItem.quantity, instantProps);
      } catch (err) {
        console.error(err);
        this._setStatus("Something went wrong. Please try again.");
        if (this.saveBtn) this.saveBtn.disabled = false;
      }
    }

    async _uploadAndAttach(lineItemKey, quantity, instantProps) {
      const finalProps = { ...instantProps };
      try {
        for (const item of this.items) {
          if (!item.hasCustomization()) continue;
          const { preview, print } = item.exportImages();
          const [previewUrl, printUrl] = await Promise.all([
            this._uploadDataUrlToCloudinary(preview),
            this._uploadDataUrlToCloudinary(print),
          ]);
          finalProps[`_${item.label} - Preview`] = previewUrl;    // underscore = hidden from customer, visible to you in order details
          finalProps[`_${item.label} - Print File`] = printUrl;   // same — raw Cloudinary links aren't useful for the customer to see
        }

        if (Object.keys(finalProps).length === 0) return; // nothing to attach

        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: lineItemKey,
            quantity: quantity || 1,
            properties: finalProps,
          }),
        });

        // Rebuild the drawer's contents again in case it's still open —
        // same event as above, safe to fire again (Impulse just re-renders
        // and no-ops the open() call if it's already open).
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      } catch (err) {
        // Deliberately quiet to the customer — the product is already in
        // their cart. Log so you can catch/reprocess failed uploads.
        console.error("Personalizer: background upload/attach failed", err);
      }
    }

    async _uploadDataUrlToCloudinary(dataUrl) {
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append("file", blob);
      formData.append("upload_preset", UPLOAD_PRESET);
      const res = await fetch(CLOUDINARY_UPLOAD_URL, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed: " + res.status);
      const data = await res.json();
      return data.secure_url;
    }

    // ---------- Trigger Impulse's own cart drawer refresh + open ----------
    _openCartDrawer() {
      // theme.CartDrawer listens for this exact event and, in response, both
      // rebuilds the drawer's HTML from the current cart AND opens it. This
      // is the same event Impulse's own native Add to Cart button relies on
      // (dispatched by theme.AjaxProduct after a successful add) — using it
      // directly means we get the theme's real behavior instead of trying
      // to reimplement it.
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
    }

    // ---------- Fallback: customer types customization but clicks the
    // store's own native Add to Cart / Buy it Now button instead of the
    // modal's button. Same non-blocking behavior applies. ----------
    _bindNativeFormFallback() {
      if (!this.productForm) return;
      this.productForm.addEventListener("submit", (e) => {
        const anyCustomized = this.items.some((i) => i.hasCustomization());
        if (!anyCustomized) return; // nothing personalized, let it submit normally
        e.preventDefault();
        this._addToCartFromModal();
      });
    }
  }

  function initAll() {
    document
      .querySelectorAll("[data-personalizer]")
      .forEach((root) => new PersonalizerManager(root));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();